import { supabase } from './supabase'

// ─── Analytics Module ────────────────────────────────────────────────────────
//
// Tracks user events in your Supabase "analytics_events" table.
// Falls back to console.log if Supabase isn't configured yet,
// so the app still works during local development.
//
// Supabase table setup (run this SQL in Supabase SQL Editor):
//
//   create table analytics_events (
//     id bigint generated always as identity primary key,
//     event_type text not null,
//     session_id text,
//     user_agent text,
//     created_at timestamptz default now()
//   );
//
//   create index idx_events_type on analytics_events(event_type);
//   create index idx_events_created on analytics_events(created_at);
//
// ─────────────────────────────────────────────────────────────────────────────

// Generate a simple session ID per browser tab
const sessionId = crypto.randomUUID?.() || Math.random().toString(36).slice(2)

/**
 * Track an analytics event.
 *
 * Event types used in the app:
 *   - "page_visit"           → User lands on the page
 *   - "openai_auth_success"  → User connects their OpenAI account
 *   - "claude_auth_success"  → User connects their Claude account
 *   - "migration_started"    → User clicks Begin Migration
 *   - "migration_completed"  → Migration finishes at 100%
 *   - "migration_failed"     → Migration errors out (future use)
 *   - "donation_clicked"     → User clicks Buy Me a Coffee
 *   - "donation_dismissed"   → User clicks Not Right Now
 */
export async function trackEvent(eventType) {
  // Always log locally for debugging
  console.log(`[Analytics] ${new Date().toISOString()} — ${eventType}`)

  try {
    const { error } = await supabase.from('analytics_events').insert({
      event_type: eventType,
      session_id: sessionId,
      user_agent: navigator.userAgent
    })

    if (error) {
      // Supabase not configured yet — that's fine during development
      console.warn('[Analytics] Supabase insert skipped:', error.message)
    }
  } catch (err) {
    // Network error or Supabase not reachable — fail silently
    console.warn('[Analytics] Could not reach Supabase:', err.message)
  }
}

/**
 * Fetch real stats for the admin dashboard.
 * Returns the same shape as the simulated data so the dashboard
 * works identically whether Supabase is connected or not.
 */
export async function fetchAdminStats() {
  try {
    // Total counts by event type
    const counts = {}
    for (const type of ['page_visit', 'openai_auth_success', 'claude_auth_success', 'migration_completed', 'migration_failed']) {
      const { count, error } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_type', type)

      if (error) throw error
      counts[type] = count || 0
    }

    // Daily breakdown for the last 7 days
    const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString()
    const { data: recentEvents, error: eventsError } = await supabase
      .from('analytics_events')
      .select('event_type, created_at')
      .gte('created_at', sevenDaysAgo)
      .order('created_at', { ascending: true })

    if (eventsError) throw eventsError

    // Group by day
    const dayMap = {}
    for (const evt of (recentEvents || [])) {
      const day = new Date(evt.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      if (!dayMap[day]) dayMap[day] = { date: day, visitors: 0, openai: 0, claude: 0, migrations: 0 }
      if (evt.event_type === 'page_visit') dayMap[day].visitors++
      if (evt.event_type === 'openai_auth_success') dayMap[day].openai++
      if (evt.event_type === 'claude_auth_success') dayMap[day].claude++
      if (evt.event_type === 'migration_completed') dayMap[day].migrations++
    }

    // Last 10 events for the activity feed
    const { data: latestEvents } = await supabase
      .from('analytics_events')
      .select('event_type, session_id, created_at')
      .order('created_at', { ascending: false })
      .limit(10)

    const eventLabels = {
      page_visit: 'New visitor',
      openai_auth_success: 'OpenAI auth',
      claude_auth_success: 'Claude auth',
      migration_started: 'Migration started',
      migration_completed: 'Migration completed',
      migration_failed: 'Migration failed',
      donation_clicked: 'Donation clicked',
      donation_dismissed: 'Donation dismissed'
    }

    const statusMap = {
      page_visit: 'info',
      openai_auth_success: 'info',
      claude_auth_success: 'info',
      migration_started: 'pending',
      migration_completed: 'success',
      migration_failed: 'error',
      donation_clicked: 'success',
      donation_dismissed: 'info'
    }

    return {
      totalVisitors: counts['page_visit'],
      openaiAuths: counts['openai_auth_success'],
      claudeAuths: counts['claude_auth_success'],
      completedMigrations: counts['migration_completed'],
      failedMigrations: counts['migration_failed'],
      avgMigrationTime: '—',
      conversionRate: counts['page_visit'] > 0
        ? ((counts['migration_completed'] / counts['page_visit']) * 100).toFixed(1) + '%'
        : '0%',
      dailyStats: Object.values(dayMap),
      recentEvents: (latestEvents || []).map(evt => ({
        time: timeAgo(evt.created_at),
        event: eventLabels[evt.event_type] || evt.event_type,
        user: evt.session_id ? `session_${evt.session_id.slice(0, 8)}` : 'unknown',
        status: statusMap[evt.event_type] || 'info'
      })),
      _isLive: true
    }
  } catch (err) {
    console.warn('[Analytics] Could not fetch admin stats — using demo data:', err.message)
    return null // Returning null signals the dashboard to use demo data
  }
}

/**
 * Simple relative time formatter
 */
function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
